import React, { createContext, useCallback, useContext, useState } from 'react';
import { CanvasDocument } from '../constants/pubsubCommands';

export type { CanvasDocument };

interface CanvasPanelApi {
  isOpen: boolean;
  document: CanvasDocument | null;
  show(): void;
  hide(): void;
  /** Load a document and open the panel. */
  loadDocument(doc: CanvasDocument): void;
  /** Close the panel and clear the loaded document. */
  clear(): void;
}

const CanvasPanelContext = createContext<CanvasPanelApi | null>(null);

export function CanvasPanelProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [document, setDocument] = useState<CanvasDocument | null>(null);

  const show = useCallback(() => setIsOpen(true), []);
  const hide = useCallback(() => setIsOpen(false), []);
  const loadDocument = useCallback((doc: CanvasDocument) => {
    setDocument(doc);
    setIsOpen(true);
  }, []);
  const clear = useCallback(() => {
    setDocument(null);
    setIsOpen(false);
  }, []);

  return (
    <CanvasPanelContext.Provider value={{ isOpen, document, show, hide, loadDocument, clear }}>
      {children}
    </CanvasPanelContext.Provider>
  );
}

export function useCanvasPanel(): CanvasPanelApi {
  const ctx = useContext(CanvasPanelContext);
  if (!ctx) {
    throw new Error('useCanvasPanel must be used inside <CanvasPanelProvider>');
  }
  return ctx;
}
